import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import type { DocumentPageMetadataUserEditEntry } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { parseDocumentPageMetadataUserEditEntries } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { cantileverDocumentPageIdForPage } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import { documentPageMetadataUserEditsByCantileverPageTag } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { selectLatestConfirmedPageMetadataUserEdit } from 'features/knowledgeDocumentViewer/utils/selectLatestConfirmedPageMetadataUserEdit'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import type { KeyedMutator } from 'swr'

export type UseDocumentPageMetadataUserEditArgs = {
  bqDocumentId: string | undefined
  pageNum: number | undefined
  enabled?: boolean
}

export function useDocumentPageMetadataUserEdit({
  bqDocumentId,
  pageNum,
  enabled = true,
}: UseDocumentPageMetadataUserEditArgs): {
  edit: DocumentPageMetadataUserEditEntry | undefined
  edits: DocumentPageMetadataUserEditEntry[]
  isLoading: boolean
  mutate: KeyedMutator<unknown>
} {
  const { siteID } = useKnowledgeDocumentDataContext()

  const loadOptions = useMemo(() => {
    if (
      !enabled ||
      !bqDocumentId ||
      pageNum === undefined ||
      pageNum < 1 ||
      !siteID
    ) {
      return undefined
    }
    return documentPageMetadataUserEditsByCantileverPageTag(
      cantileverDocumentPageIdForPage(siteID, bqDocumentId, pageNum),
      { site: siteID }
    )
  }, [bqDocumentId, enabled, pageNum, siteID])

  const {
    data: rawEntries,
    isLoading,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<unknown>(loadOptions)

  const edits = useMemo(() => {
    if (rawEntries === undefined) return []
    return parseDocumentPageMetadataUserEditEntries(rawEntries)
  }, [rawEntries])

  const edit = useMemo(() => {
    return selectLatestConfirmedPageMetadataUserEdit(edits)
  }, [edits])

  return {
    edit,
    edits,
    isLoading: enabled && loadOptions !== undefined && isLoading,
    mutate,
  }
}
